// web/src/admin/AdminHealth.tsx (/admin/health) — 50_ADMIN_CONSOLE.md §3.4.
//
// ⚠️ THE `needs_attention` QUEUE AT THE TOP OF THIS PAGE IS A PRODUCTION RELEASE VALVE, NOT A
// REPORT. Read 90_OPEN_ITEMS D-14 before changing it:
//
//   While a request sits at `needs_attention`, doc 22 §8's one-preview-per-site guard REFUSES
//   every new update for that site with a 409. The customer cannot publish their way out (there
//   is no approved preview), cannot fold the ask into the chat (the row is parked, not working),
//   and nothing times out. `adminRetry` is the only release in the entire system.
//
// So the queue is built to be acted on, not read: each row states which org and which site is
// blocked, how long it has been blocked, exactly which gate checks are red, how many repair
// passes were already spent, a link to the preview, and a Retry button INLINE. Nothing here
// requires opening a second screen to decide.
//
// D-13 rides along: those same rows never settle, so their spend is never charged. The unbilled
// total is stated at the top — "unbilled beats wrongly billed" is only the right trade while
// somebody can see how much of it there is.
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminBlockedRequest } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { KPICard } from '../components/KPICard';
import { Icon } from '../components/Icon';
import { toast } from '../components/Toast';
import { FEATURES } from '../lib/features';
import { WindowToggle, blockedFor, pct, usd, useAdminWindow } from './adminShared';

export function AdminHealth() {
  const [days, setDays] = useAdminWindow();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'health', days],
    queryFn: () => api.adminHealth(days),
    refetchInterval: 30_000,
  });

  async function retry(r: AdminBlockedRequest) {
    try {
      await api.adminRetryRequest(r.requestId);
      toast({
        kind: 'success',
        message: `Retrying #${r.displayNo ?? ''} — ${r.siteName ?? 'the site'} accepts new updates again.`,
      });
      qc.invalidateQueries({ queryKey: ['admin'] });
    } catch {
      toast({ kind: 'error', message: "Couldn't retry. Open the request and check its error." });
    }
  }

  return (
    <>
      <PageHeader
        title="Health"
        subtitle="What is blocked, what is stalling, and what it is costing while it waits."
      />
      <div className="mb-4">
        <WindowToggle days={days} onChange={setDays} />
      </div>

      <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
        {(h) => (
          <>
            <section className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KPICard
                label="Sites blocked"
                value={h.needsAttention.length}
                delta={h.needsAttention.length ? 'no customer escape' : undefined}
                accent={h.needsAttention.length ? 'danger' : 'info'}
              />
              <KPICard
                label="Unbilled while held"
                value={usd(h.unbilledCents)}
                delta="never settles"
                accent={h.unbilledCents ? 'warn' : 'info'}
              />
              <KPICard label="Gate errors" value={h.gateErrors} accent={h.gateErrors ? 'warn' : 'info'} />
              <KPICard
                label="Orgs with no budget"
                value={h.unprovisionedOrgs}
                accent={h.unprovisionedOrgs ? 'warn' : 'info'}
              />
            </section>

            {/* ── The release valve ───────────────────────────────────────────────────────── */}
            <section className="mb-6">
              <h2 className="mb-1 font-head text-sm text-ink">Needs attention — blocked sites</h2>
              <p className="mb-3 text-xs text-muted">
                Each of these blocks <b>every</b> new update for its site until you retry it. The
                customer cannot clear it and it does not time out.
              </p>
              {h.needsAttention.length === 0 ? (
                <p className="flex items-center justify-center gap-2 rounded-lg border border-hair bg-surface/40 px-4 py-8 text-sm text-muted">
                  <Icon name="check" size={16} className="text-success" />
                  Nothing is blocked.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {h.needsAttention.map((r) => (
                    <li key={r.requestId} className="rounded-lg border border-warn bg-warn/5 p-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-ink">
                            {r.displayNo != null && (
                              <span className="mr-1.5 font-head text-xs text-muted">#{r.displayNo}</span>
                            )}
                            {r.title}
                          </span>
                          <span className="block truncate text-xs text-muted">
                            <Link to={`/admin/customers/${r.orgId}`} className="underline underline-offset-2">
                              {r.orgName}
                            </Link>{' '}
                            · {r.siteName ?? 'no site'} · blocked{' '}
                            <b className="text-warn">{blockedFor(r.blockedHours)}</b>
                            {r.blockedBehind > 0 && ` · ${r.blockedBehind} other open on this site`}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5">
                          {r.previewUrl && (
                            <a
                              href={r.previewUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex min-h-10 items-center gap-1 rounded-md border border-hair px-2.5 text-xs text-ink"
                            >
                              <Icon name="external" size={14} />
                              Preview
                            </a>
                          )}
                          {FEATURES.chalice && ( // Retry re-runs chalice: hidden (portal spec 10)
                            <button
                              onClick={() => retry(r)}
                              className="inline-flex min-h-10 items-center gap-1 rounded-md bg-run px-3 text-xs font-medium text-runink"
                            >
                              <Icon name="refresh" size={14} />
                              Retry
                            </button>
                          )}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                        {r.gateFailures.length ? (
                          r.gateFailures.map((f) => (
                            <span key={f} className="rounded-pill border border-danger px-1.5 py-0.5 text-danger">
                              {f}
                            </span>
                          ))
                        ) : (
                          <span className="rounded-pill border border-hair px-1.5 py-0.5 text-muted">
                            no failure list recorded
                          </span>
                        )}
                        <span className="rounded-pill border border-hair px-1.5 py-0.5 text-muted">
                          {r.gateFixAttempts} repair pass{r.gateFixAttempts === 1 ? '' : 'es'} spent
                        </span>
                        <span className="rounded-pill border border-hair px-1.5 py-0.5 text-muted">
                          {usd(r.unbilledCents)} unbilled
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ── Stalls the reaper is sweeping ───────────────────────────────────────────── */}
            <section className="mb-6">
              <h2 className="mb-3 font-head text-sm text-muted">Stalled past their phase budget</h2>
              {h.stalled.length === 0 ? (
                <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">
                  Nothing stalled.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {h.stalled.map((r) => (
                    <li
                      key={r.requestId}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-hair bg-surface p-3"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-ink">{r.title}</span>
                        <span className="block truncate text-xs text-muted">
                          {r.orgName} · {r.siteName ?? '—'} · quiet {blockedFor(r.blockedHours)}
                        </span>
                      </span>
                      {FEATURES.chalice && (
                        <button
                          onClick={() => retry(r)}
                          className="inline-flex min-h-10 shrink-0 items-center gap-1 rounded-md border border-hair px-2.5 text-xs text-ink"
                        >
                          <Icon name="refresh" size={14} />
                          Retry
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mb-6 grid gap-4 lg:grid-cols-2">
              <div>
                <h2 className="mb-3 font-head text-sm text-muted">Failures by phase (24h)</h2>
                <ul className="flex flex-col gap-1.5">
                  {h.failuresByPhase.map((f) => (
                    <li
                      key={f.phase}
                      className="flex items-center justify-between rounded-md border border-hair bg-surface px-3 py-2 text-sm"
                    >
                      <span className="text-ink">{f.phase}</span>
                      <b className="font-head text-danger">{f.count}</b>
                    </li>
                  ))}
                  {h.failuresByPhase.length === 0 && (
                    <li className="rounded-md border border-hair bg-surface/40 px-3 py-6 text-center text-sm text-muted">
                      No failures in the last 24 hours.
                    </li>
                  )}
                </ul>
              </div>
              <div>
                <h2 className="mb-1 font-head text-sm text-muted">Foundry throttling</h2>
                <p className="mb-3 text-[11px] text-muted">{h.throttleScope}</p>
                <ul className="flex flex-col gap-1.5">
                  {h.throttle.map((t) => (
                    <li
                      key={t.model}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hair bg-surface px-3 py-2 text-sm"
                    >
                      <span className="font-head text-ink">{t.model}</span>
                      <span className="flex items-center gap-3 text-xs text-muted">
                        <span>{t.calls} calls</span>
                        <b className={t.throttled ? 'font-head text-warn' : 'font-head text-ink'}>
                          {pct(t.calls ? t.throttled / t.calls : null, 1)} 429
                        </b>
                      </span>
                    </li>
                  ))}
                  {h.throttle.length === 0 && (
                    <li className="rounded-md border border-hair bg-surface/40 px-3 py-6 text-center text-sm text-muted">
                      No direct-SDK calls yet in this process.
                    </li>
                  )}
                </ul>
              </div>
            </section>

            <section>
              <h2 className="mb-1 font-head text-sm text-muted">Over-cap requests</h2>
              <p className="mb-3 text-[11px] text-muted">
                Billable spend passed the 200-credit ceiling (02 §2). Charged at the cap; flagged
                for review, not silently billed.
              </p>
              {h.overCap.length === 0 ? (
                <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">
                  Nothing over the cap in this window.
                </p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {h.overCap.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hair bg-surface px-3 py-2 text-sm"
                    >
                      <span className="min-w-0 truncate text-ink">
                        {r.displayNo != null && <span className="mr-1.5 text-xs text-muted">#{r.displayNo}</span>}
                        {r.title}
                      </span>
                      <span className="flex items-center gap-3 text-xs text-muted">
                        {r.complexity && <span>{r.complexity}</span>}
                        <b className="font-head text-danger">{usd(r.aiSpendCents)}</b>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </AsyncBoundary>
    </>
  );
}
